import { cn, hasMessage } from '@/lib/utils';
import {
  MutableRefObject,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  ChainlitContext,
  FileSpec,
  useChatMessages,
  useChatSession,
  useConfig
} from '@chainlit/react-client';

import { Logo } from '@/components/Logo';
import { Markdown } from '@/components/Markdown';

import MessageComposer from './MessageComposer';
import Starters from './Starters';

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: File[]) => void;
  onFileUploadError: (error: string) => void;
  autoScrollRef: MutableRefObject<boolean>;
}

export default function WelcomeScreen(props: Props) {
  const apiClient = useContext(ChainlitContext);
  const { config } = useConfig();
  const { chatProfile } = useChatSession();
  const { messages } = useChatMessages();
  const [isVisible, setIsVisible] = useState(false);

  const chatProfiles = config?.chatProfiles;
  const allowHtml = config?.features?.unsafe_allow_html;
  const latex = config?.features?.latex;

  // ✅ TOUJOURS appeler tous les hooks AVANT les returns conditionnels
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const logo = useMemo(() => {
    if (chatProfile && chatProfiles) {
      const currentChatProfile = chatProfiles.find(
        (cp) => cp.name === chatProfile
      );
      if (currentChatProfile?.icon) {
        return (
          <div className="flex flex-col gap-2 mb-2 items-center">
            <img
              className="h-16 w-16 rounded-full"
              src={
                currentChatProfile?.icon.startsWith('/public')
                  ? apiClient.buildEndpoint(currentChatProfile?.icon)
                  : currentChatProfile?.icon
              }
              alt={currentChatProfile.display_name || currentChatProfile.name}
            />
            {currentChatProfile?.markdown_description ? (
              <Markdown allowHtml={allowHtml} latex={latex}>
                {currentChatProfile.markdown_description}
              </Markdown>
            ) : null}
          </div>
        );
      }
    }

    return <Logo className="w-[200px] mb-2" />;
  }, [chatProfiles, chatProfile, apiClient, allowHtml, latex]);

  // ✅ APRÈS tous les hooks : returns conditionnels

  // Si pas d'agent sélectionné et plusieurs profils disponibles
  if (!chatProfile && chatProfiles && chatProfiles.length > 1) {
    return (
      <div className="flex items-center justify-center h-full -mt-[60px]">
        <div className="text-center space-y-4">
          <p className="text-2xl">👆</p>
          <p className="text-lg font-semibold">Veuillez sélectionner un agent</p>
          <p className="text-muted-foreground">
            Cliquez sur le menu ci-dessus pour choisir l'agent qui correspond à vos besoins
          </p>
        </div>
      </div>
    );
  }

  // Si des messages existent, ne rien afficher
  if (hasMessage(messages)) {
    return null;
  }

  // Affichage du WelcomeScreen normal avec logo, composer et starters
  return (
    <div
      id="welcome-screen"
      className={cn(
        'flex flex-col -mt-[60px] gap-4 w-full flex-grow items-center justify-center welcome-screen mx-auto transition-opacity duration-500 opacity-0 delay-100',
        isVisible && 'opacity-100'
      )}
    >
      {logo}
      <MessageComposer {...props} />
      <Starters />
    </div>
  );
}